import { Fragment, useId, useState } from 'react'
import { GateShell } from './GateShell'
import type { GateAnswer, GateOption } from './GateShell'
import type { GateAnswered } from '../../stream/reducer'

interface ClarifyCardProps {
  payload: Record<string, unknown>
  options: readonly GateOption[]
  busy: boolean
  from: string
  answer: GateAnswered | null
  onAnswer: (answer: GateAnswer) => void
}

/** One question the specialist asked. `AskUserQuestion`'s schema, `Specification/03 §6.6`. */
interface Question {
  question: string
  header: string
  options: string[]
  multiSelect: boolean
}

/**
 * THE CLARIFY CARD. `Specification/08 §5.1`, `AC-UI-3`.
 *
 * <p>The questions were rendered as a READ-ONLY LIST, with one card-level verdict and one free-text
 * box underneath. A reader could see what was asked and had no way to answer it except in prose —
 * which is the opposite of what this gate is for.</p>
 *
 * <p><b>A FREE-TEXT FIELD ON EVERY QUESTION, ALWAYS</b>, regardless of how good the options look:
 * "Parameter negotiation is open-ended reasoning about values; four options will frequently not
 * contain the answer, and <b>a user forced to pick the nearest wrong one corrupts the
 * binding</b>."</p>
 *
 * <p><b>THE LITERAL WORD "OTHER" IS NEVER RECORDED.</b> Selecting an option puts its LABEL in
 * `answers`; typing puts the RAW STRING. "Other" is not an answer, it is the absence of one, and a
 * binding whose provenance reads `Other` names nothing.</p>
 */
export function ClarifyCard({ payload, options, busy, from, answer, onAnswer }
  : ClarifyCardProps): React.JSX.Element {
  const questions = parse(payload['questions'])
  const group = useId()

  // ONE ANSWER PER QUESTION, by index. A chosen option and typed text are held apart so that
  // typing does not silently clear a selection the reader may come back to.
  const [chosen, setChosen] = useState<Record<number, string[]>>({})
  const [typed, setTyped] = useState<Record<number, string>>({})

  // "REPLY INSTEAD…" SETS `response`, NOT `answers` — for a reply that answers no specific
  // question. It is a different act from answering badly, and the specialist is told which it was.
  const [replying, setReplying] = useState(false)

  const answerFor = (i: number): string[] => {
    const text = (typed[i] ?? '').trim()
    if (text.length > 0) return [text]
    return chosen[i] ?? []
  }

  const answered = questions.map((_, i) => answerFor(i)).filter((a) => a.length > 0).length
  const complete = questions.length === 0 || answered === questions.length

  if (answer !== null || questions.length === 0) {
    // AN ANSWERED CARD SHOWS THE QUESTION AND THE ANSWER — not the question and every option it
    // offered. What was OFFERED is what was ASKED; a reader coming back to a card they answered
    // wants to know what they SAID, and four questions' option lists run together into one block
    // of text that answers nothing.
    return (
      <GateShell kind="clarify" origin="agent" state="Awaiting you" options={options}
                 busy={busy} onAnswer={onAnswer} answer={answer} from={from}
                 heading="A few things I need settled before I can size this">
        <Answered questions={questions} answer={answer} />
      </GateShell>
    )
  }

  return (
    <section className="gate open" role="group" aria-labelledby={`${group}-h`}>
      <header>
        <span className="kind" id={`${group}-h`}>Clarify</span>
        <span className="from">raised by the {from}</span>
        <span className="state">Awaiting you</span>
      </header>

      <div className="body">
        <h3>A few things I need settled before I can size this</h3>

        {questions.map((q, i) => (
          <fieldset className="opts qn" key={i}>
            {/* `header`, ≤ 12 characters, as the question's own label. */}
            <legend className="mlabel">{q.header}</legend>
            <p className="qn__text">{q.question}</p>

            {q.options.map((option) => {
              const picked = (chosen[i] ?? []).includes(option)
              return (
                <label className="opt" key={option}>
                  <input type={q.multiSelect ? 'checkbox' : 'radio'}
                         name={`${group}-${i}`} value={option} checked={picked}
                         onChange={(event) => {
                           setTyped((t) => ({ ...t, [i]: '' }))
                           setChosen((c) => ({
                             ...c,
                             [i]: q.multiSelect
                               ? event.target.checked
                                 ? [...(c[i] ?? []), option]
                                 : (c[i] ?? []).filter((o) => o !== option)
                               : [option],
                           }))
                         }} />
                  <span>{option}</span>
                </label>
              )
            })}

            {/* ALWAYS. And it is not labelled "Other": the label describes what the field is for,
                because the word "Other" is the one thing that must never be recorded. */}
            <div className="freetext">
              <label htmlFor={`${group}-${i}-text`}>
                Something the options do not cover, recorded verbatim
              </label>
              <input id={`${group}-${i}-text`} type="text" value={typed[i] ?? ''}
                     onChange={(event) => {
                       const text = event.target.value
                       setTyped((t) => ({ ...t, [i]: text }))
                       if (text.trim().length > 0) setChosen((c) => ({ ...c, [i]: [] }))
                     }} />
            </div>
          </fieldset>
        ))}

        {replying && (
          <div className="freetext">
            <label htmlFor={`${group}-reply`}>
              Your reply. Recorded verbatim, and it answers no specific question above.
            </label>
            <textarea id={`${group}-reply`} rows={3} value={typed[-1] ?? ''}
                      onChange={(event) => setTyped((t) => ({ ...t, [-1]: event.target.value }))} />
          </div>
        )}

        <div className="actions">
          <button type="button" className="btn primary"
                  disabled={busy || (replying
                    ? (typed[-1] ?? '').trim().length === 0
                    : !complete)}
                  onClick={() => onAnswer(replying
                    ? {
                        decision: 'answered',
                        extra: { response: (typed[-1] ?? '').trim() },
                      }
                    : {
                        decision: 'answered',

                        // ONE ENTRY PER QUESTION, IN ORDER, carrying the LABEL or the raw string.
                        // The question travels with it so the specialist is not matching by
                        // position across a card it cannot see.
                        extra: {
                          answers: questions.map((q, i) => ({
                            question: q.question,
                            header: q.header,
                            answer: answerFor(i),
                          })),
                        },
                      })}>
            {busy ? 'Sending…' : 'Submit'}
          </button>

          <button type="button" className="btn quiet"
                  onClick={() => setReplying((was) => !was)}>
            {replying ? 'Answer the questions instead' : 'Reply instead…'}
          </button>

          <span className="note" style={{ alignSelf: 'center' }}>
            {replying
              ? 'A reply is recorded as a reply, not as an answer to any of these.'
              : 'Every question takes an answer or a sentence.'}
          </span>
        </div>
      </div>
    </section>
  )
}

/**
 * WHAT WAS ASKED AND WHAT WAS ANSWERED, for a card nobody can change any more.
 *
 * <p>The answers come off the frame, not off this page's memory: a reader who opens the engagement
 * tomorrow sees the same card as the one who answered it, and a card that could only show answers
 * it had watched being typed would be blank on every reload.</p>
 */
function Answered({ questions, answer }:
                  { questions: readonly Question[]; answer: GateAnswered | null })
  : React.JSX.Element {
  // "REPLY INSTEAD…" ANSWERED NO SPECIFIC QUESTION, and saying so is the whole point of it being
  // a separate act. Pairing it with the questions would imply it answered them.
  if (answer?.response) {
    return (
      <>
        <dl className="qa">
          {questions.map((q, i) => <dt key={i}>{q.question}</dt>)}
        </dl>
        <p className="replied">
          <span className="mlabel">Replied instead</span> “{answer.response}”
        </p>
      </>
    )
  }

  const given = answer?.answers ?? null

  return (
    <dl className="qa">
      {/* A FLAT `<dl>`, which is what `.qa` is written for and what the mockup draws. Wrapping
          each pair in a `<div>` made EVERY `<dd>` the last child of its own wrapper, so
          `.qa dd:last-child{margin-bottom:0}` matched all of them and collapsed the spacing
          between every question and the next — the card read as one block of text. */}
      {questions.map((q, i) => {
        const said = given?.find((a) => a.question === q.question)?.answer
                     ?? given?.[i]?.answer ?? null
        return (
          <Fragment key={i}>
            <dt>{q.question}</dt>
            <dd>
              {Array.isArray(said) && said.length > 0
                ? said.join(' · ')

                // NOT THE OPTIONS. An answer the frame did not carry is an answer this page does
                // not have, and printing what was offered in its place would read as what was
                // chosen — which is the defect this replaced.
                : <span className="note">not recorded on this card</span>}
            </dd>
          </Fragment>
        )
      })}
    </dl>
  )
}

/**
 * The questions, defensively.
 *
 * <p>A card that could not parse its own payload used to render an empty list and a Submit button,
 * which asks a person to answer nothing. An unparseable question is DROPPED and the rest are still
 * answerable — and if none survive, the shell's own verdict is still there.</p>
 */
function label(option: unknown): string[] {
  if (typeof option !== 'object' || option === null) return []
  const text = (option as Record<string, unknown>)['label']
  return typeof text === 'string' && text.length > 0 ? [text] : []
}

function parse(value: unknown): Question[] {
  if (!Array.isArray(value)) return []

  return value.flatMap((raw) => {
    if (typeof raw !== 'object' || raw === null) return []
    const q = raw as Record<string, unknown>

    const question = typeof q['question'] === 'string' ? q['question'] : null
    if (question === null || question.length === 0) return []

    const options = Array.isArray(q['options'])
      ? q['options'].flatMap((o) =>
          typeof o === 'string' ? [o]
          : label(o))
      : []

    return [{
      question,
      header: typeof q['header'] === 'string' ? q['header'] : 'Question',
      options,
      multiSelect: q['multiSelect'] === true,
    }]
  })
}
